import type { FrameworkDelegate, JSX } from '@familyjs/core/components';
import { defineCustomElement } from '@familyjs/core/components/fml-nav.js';
import React, { useMemo, useState } from 'react';

import { ReactDelegate } from '../../framework-delegate';
import { createReactComponent } from '../react-component-lib';
import { createForwardRef } from '../utils';

const FmlNavInner = createReactComponent<JSX.FmlNav & { delegate: FrameworkDelegate }, HTMLFmlNavElement>(
  'fml-nav',
  undefined,
  undefined,
  defineCustomElement
);

type FmlNavProps = JSX.FmlNav & {
  forwardedRef?: React.ForwardedRef<HTMLFmlNavElement>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FmlNavInternal: React.FC<FmlNavProps> = ({ children, forwardedRef, ...restOfProps }) => {
  const [views, setViews] = useState<React.ReactElement[]>([]);

  /**
   * Allows us to create React components that are rendered within
   * the context of the FmlNav component.
   */
  const addView = (view: React.ReactElement) => setViews((existingViews) => [...existingViews, view]);
  const removeView = (view: React.ReactElement) => setViews((existingViews) => existingViews.filter((v) => v !== view));

  const delegate = useMemo(() => ReactDelegate(addView, removeView), []);

  return (
    <FmlNavInner delegate={delegate} ref={forwardedRef} {...restOfProps}>
      {views}
    </FmlNavInner>
  );
};

export const FmlNav = createForwardRef<FmlNavProps, HTMLFmlNavElement>(FmlNavInternal, 'FmlNav');
